import { useFormik } from "formik";
import { AiOutlineInfoCircle } from "react-icons/ai";
import * as yup from "yup";
import dayjs from "dayjs";
import Breadcrumb from "../../components/Breadcrumb";
import { useToast } from "../../components/Toast/toastHook";
import NotificationForm from "../../components/administrator/NotificationForm";
import { useMutation, useQuery } from "@apollo/client";
import {
  GET_NOTIFICATION_USERS,
  SEND_NOTIFICATION,
} from "../../graphql/notificationQueries";
import { GET_COMPANIES_LISTING } from "../../graphql/companyQueries";
import { useEffect, useRef, useState } from "react";
import NotificationsHistory, {
  useNotificationsHistory,
} from "./NotificationsHistory";
import SentNotificationsTable from "./SentNotificationsTable";
import NotificationsTabs from "../../components/NotificationsTabs";
import { getStoredRoleName } from "../../utils/notificationNav";

const Notifications = () => {
  const toast = useToast(4000);
  const [tab, setTab] = useState("received");
  const canSeeSent = getStoredRoleName() !== "AGENT";
  const history = useNotificationsHistory();

  const [sendNotification, { data, loading, error }] = useMutation(
    SEND_NOTIFICATION,
    {
      // Refresh the Sent list so the new notification appears without a reload.
      refetchQueries: ["getSentNotifications"],
      onCompleted(result) {
        toast("success", result?.sendNotification || "Notification sent successfully!");
        // Land the admin on what they just sent.
        if (canSeeSent) setTab("sent");
      },
      onError(e) {
        console.error(e);
        toast("error", e?.message ?? "Something went wrong!");
      },
    }
  );

  const formik = useFormik({
    initialValues: {
      title: "",
      description: "",
      loading: false,
      toEveryOne: false,
      sendMode: "now",
      scheduledDate: null,
      scheduledTime: null,
      search: {
        company: "",
        user: "",
      },
      isOpen: {
        company: false,
        user: false,
      },
      selectedItems: { company: [], user: [] },
      companies: [],
      users: [],
    },
    validationSchema: yup.object().shape({
      title: yup.string().required("Title cannot be empty").max(100),
      description: yup.string().required("Description is Required").max(500),
    }),
    onSubmit: async (values, helper) => {
      let scheduledAtISO = null;
      if (values.sendMode === "schedule") {
        if (!values.scheduledDate || !values.scheduledTime) {
          formik.setFieldError(
            "scheduledAt",
            "Pick a date and time to schedule this notification."
          );
          return;
        }
        const combined = values.scheduledDate
          .hour(values.scheduledTime.hour())
          .minute(values.scheduledTime.minute())
          .second(0)
          .millisecond(0);
        if (combined.isBefore(dayjs())) {
          formik.setFieldError("scheduledAt", "Pick a time in the future.");
          return;
        }
        scheduledAtISO = combined.toISOString();
      }

      formik.setFieldValue("loading", true);
      let notificationInput = {
        body: values.description,
        title: values.title,
      };
      if (values.selectedItems.company.length > 0) {
        notificationInput = {
          ...notificationInput,
          companyIDs: values.selectedItems.company,
        };
      }
      if (values.selectedItems.user.length > 0) {
        notificationInput = {
          ...notificationInput,
          userIDs: values.selectedItems.user,
        };
      }
      if (scheduledAtISO) {
        notificationInput = { ...notificationInput, scheduledAt: scheduledAtISO };
      }
      sendNotification({
        variables: {
          notificationInput,
        },
      });
      helper.resetForm();
      formik.setFieldValue("loading", false);
      populateData();
    },
  });

  const companyRes = useQuery(GET_COMPANIES_LISTING, {
    fetchPolicy: "no-cache",
  });
  
  const userRes = useQuery(GET_NOTIFICATION_USERS, {
    fetchPolicy: "no-cache",
  });

  const dropdownRef = useRef(null);

  const populateData = () => {
    if (companyRes.data) {
      formik.setFieldValue("companies", companyRes.data.getAllCompanyList);
    }
    if (userRes.data) {
      formik.setFieldValue("users", userRes.data.getNotificationUsers);
    }
  };
  useEffect(() => {
    if (companyRes.refetch) {
      companyRes.refetch();
    }
    if (userRes.refetch) {
      userRes.refetch();
    }
    populateData();
  }, [companyRes.data, userRes.data]);

  useEffect(() => {
    const handleBodyClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        formik.setFieldValue("isOpen.company", false);
        formik.setFieldValue("isOpen.user", false);
      }
    };
    document.addEventListener("mousedown", handleBodyClick);

    return () => {
      document.removeEventListener("mousedown", handleBodyClick);
    };
  }, []);

  return (
    <div id="Notifications">
      <Breadcrumb pageName="Notifications" />
      <NotificationsTabs
        tab={tab}
        onChange={setTab}
        unreadCount={history.unreadCount}
        showSent={canSeeSent}
      />
      {tab === "compose" && (
        <>
          <div className="flex gap-2 items-start md:items-center flex-col md:flex-row text-slate-400">
            <AiOutlineInfoCircle size="18" />
            <p className="text-sm">
              Facilitates the broadcasting of notifications to all registered
              users of the mobile application.
            </p>
          </div>
          <NotificationForm formik={formik} dropdownRef={dropdownRef} />
        </>
      )}
      {tab === "received" && <NotificationsHistory history={history} />}
      {tab === "sent" && canSeeSent && <SentNotificationsTable />}
    </div>
  );
};

export default Notifications;
